import { useMutation } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type QuestionCategory = Database['public']['Enums']['question_types'];

export interface CategoryRequest {
  category?: QuestionCategory;
  category_code?: string; // paper-specific category code (e.g., 'LANG-P2-Q1')
  question_count: number;
  has_images?: boolean; // Optional: filter Section B questions by image presence
}

interface CreateMixedPracticeSetParams {
  authId: string;
  categories: CategoryRequest[];
  totalSetSize?: number;
  questionCategory?: string;
}

interface CreateMixedPracticeSetResponse {
  message: string;
  practice_set_id: string;
  question_count: number;
  categories_included: string[];
}

const useCreateMixedPracticeSet = () => {
  const mutationFn = async (params: CreateMixedPracticeSetParams): Promise<CreateMixedPracticeSetResponse> => {
    const response = await supabaseClient.functions.invoke('create-mixed-practice-set', {
      body: {
        auth_id: params.authId,
        categories: params.categories,
        total_set_size: params.totalSetSize,
        question_category: params.questionCategory,
      },
    });

    if (response.error) {
      // Try to extract error message from response data (for non-2xx responses)
      const errorMessage = (response.data as any)?.errors?.[0] || response.error.message;
      if (response.error?.context?.status === 429) {
        throw new Error("You've reached your daily limit of 25 questions. Your limit will reset at midnight.");
      }
      throw new Error(errorMessage);
    }

    return response.data.data;
  };

  return useMutation({
    mutationFn,
  });
};

export default useCreateMixedPracticeSet;
